/**
 * Constantes compartidas entre main, server y renderer.
 */

/**
 * Sprint 63 (Fase 5) — Los cuatro oficios del negocio.
 *
 * La fuente de verdad de qué puede cada uno es `shared/permisos.ts`; aquí sólo viven los
 * nombres para no crear una dependencia circular con las constantes de canales.
 */
export const ROLES = ['dueño', 'caja', 'cocina', 'mesero'] as const;
export type Rol = (typeof ROLES)[number];

export const TIPOS_PEDIDO = ['comer_aqui', 'delivery'] as const;
export type TipoPedido = (typeof TIPOS_PEDIDO)[number];

export const ESTADOS_PEDIDO = [
  'abierto',
  'pendiente_cocina',
  'cocina',
  'listo',
  // Sprint 8 — Despacho a repartidor (delivery only)
  'en_ruta',
  'entregado',
  'no_entregado',
  'pagado',
  'cancelado',
] as const;
export type EstadoPedido = (typeof ESTADOS_PEDIDO)[number];

export const ESTADOS_MESA = ['disponible', 'ocupada', 'pagando'] as const;
export type EstadoMesa = (typeof ESTADOS_MESA)[number];

export const METODOS_PAGO = ['efectivo', 'tarjeta', 'transferencia'] as const;
export type MetodoPago = (typeof METODOS_PAGO)[number];

export const TIPOS_PRODUCTO = ['corte', 'pollo', 'complemento', 'paquete'] as const;
export type TipoProducto = (typeof TIPOS_PRODUCTO)[number];

/**
 * PINs triviales que no se permiten al dueño.
 * Why: protege contra ingeniería social básica + adivinanzas obvias.
 */
export const PINS_BLOQUEADOS = [
  '0000', '1111', '2222', '3333', '4444', '5555', '6666', '7777', '8888', '9999',
  '1234', '4321', '0123', '9876',
];

export interface Moneda {
  codigo: string;
  simbolo: string;
  locale: string;
  label: string;
}

export const MONEDAS: Moneda[] = [
  { codigo: 'MXN', simbolo: '$', locale: 'es-MX', label: 'Peso Mexicano' },
  { codigo: 'USD', simbolo: '$', locale: 'en-US', label: 'Dólar Americano' },
  { codigo: 'EUR', simbolo: '€', locale: 'es-ES', label: 'Euro' },
  { codigo: 'GTQ', simbolo: 'Q', locale: 'es-GT', label: 'Quetzal' },
  { codigo: 'COP', simbolo: '$', locale: 'es-CO', label: 'Peso Colombiano' },
  { codigo: 'ARS', simbolo: '$', locale: 'es-AR', label: 'Peso Argentino' },
  { codigo: 'CLP', simbolo: '$', locale: 'es-CL', label: 'Peso Chileno' },
  { codigo: 'PEN', simbolo: 'S/', locale: 'es-PE', label: 'Sol Peruano' },
];

export interface ColorOption {
  hex: string;
  label: string;
}

export const PALETA_BRANDING: ColorOption[] = [
  { hex: '#1976D2', label: 'Azul' },
  { hex: '#388E3C', label: 'Verde' },
  { hex: '#D32F2F', label: 'Rojo' },
  { hex: '#F57C00', label: 'Naranja' },
  { hex: '#7B1FA2', label: 'Morado' },
  { hex: '#00796B', label: 'Teal' },
  { hex: '#5D4037', label: 'Café' },
  { hex: '#212121', label: 'Negro' },
];

/**
 * Canales IPC. Se exponen vía Context Bridge en preload.ts.
 * Naming: <recurso>:<accion>
 */
export const IPC_CHANNELS = {
  // Onboarding
  ONBOARDING_ESTADO: 'onboarding:estado',
  ONBOARDING_GUARDAR_PASO: 'onboarding:guardar-paso',
  ONBOARDING_COMPLETAR: 'onboarding:completar',
  ONBOARDING_CARGAR_DEMO: 'onboarding:cargar-demo',

  // Auth
  AUTH_LISTAR_USUARIOS: 'auth:listar-usuarios',
  AUTH_LOGIN_MESERO: 'auth:login-mesero',
  AUTH_LOGIN_DUENO: 'auth:login-dueño',
  AUTH_LOGOUT: 'auth:logout',
  AUTH_VERIFICAR_PIN: 'auth:verificar-pin',

  // Negocio
  NEGOCIO_OBTENER: 'negocio:obtener',
  NEGOCIO_ACTUALIZAR: 'negocio:actualizar',
  NEGOCIO_GUARDAR_LOGO: 'negocio:guardar-logo',
  /**
   * Sprint 107 — La imagen que sale por la impresora térmica.
   *
   * Canal aparte del logo de pantalla porque son **dos decisiones distintas**: la térmica no
   * imprime color, y un logo oscuro sale de ella como un cuadro negro. El negocio sube aquí su
   * versión de contornos sin tocar el logo que ve todo el mundo.
   */
  NEGOCIO_GUARDAR_LOGO_TICKET: 'negocio:guardar-logo-ticket',

  // Configuración
  CONFIG_OBTENER: 'config:obtener',
  CONFIG_ACTUALIZAR: 'config:actualizar',

  // Categorías
  CATEGORIAS_LISTAR: 'categorias:listar',
  CATEGORIAS_CREAR: 'categorias:crear',
  CATEGORIAS_ACTUALIZAR: 'categorias:actualizar',
  CATEGORIAS_ELIMINAR: 'categorias:eliminar',

  // Productos
  PRODUCTOS_LISTAR: 'productos:listar',
  PRODUCTOS_CREAR: 'productos:crear',
  PRODUCTOS_ACTUALIZAR: 'productos:actualizar',
  PRODUCTOS_ELIMINAR: 'productos:eliminar',

  // Usuarios (ABM dueño)
  // Sprint 136 — Presencia y chat interno. Van por IPC para que el bridge los dé también al web.
  PRESENCIA_MARCAR: 'presencia:marcar',
  CHAT_LISTAR: 'chat:listar',
  CHAT_ENVIAR: 'chat:enviar',
  CHAT_MARCAR_LEIDOS: 'chat:marcar-leidos',
  // Sprint 136 — La llave del ayudante que compra el propio negocio.
  CONFIG_ESTADO_API_KEY: 'config:estado-api-key',
  CONFIG_GUARDAR_API_KEY: 'config:guardar-api-key',
  USUARIOS_LISTAR: 'usuarios:listar',
  USUARIOS_CREAR: 'usuarios:crear',
  USUARIOS_ACTUALIZAR: 'usuarios:actualizar',
  USUARIOS_ELIMINAR: 'usuarios:eliminar',
  // Sprint 9.2 — cambio de PIN con re-auth
  USUARIOS_CAMBIAR_PIN: 'usuarios:cambiar-pin',
  // Sprint 9.4 — 2FA TOTP
  USUARIOS_TOTP_ESTADO: 'usuarios:totp-estado',
  USUARIOS_TOTP_GENERAR_SECRET: 'usuarios:totp-generar-secret',
  USUARIOS_TOTP_ACTIVAR: 'usuarios:totp-activar',
  USUARIOS_TOTP_DESACTIVAR: 'usuarios:totp-desactivar',
  USUARIOS_TOTP_REGENERAR_BACKUP: 'usuarios:totp-regenerar-backup',
  AUTH_VERIFICAR_2FA: 'auth:verificar-2fa',
  // Sprint 32 — Step-up re-verificación 2FA para acciones críticas post-login.
  AUTH_REQUIERE_STEP_UP: 'auth:requiere-step-up',
  AUTH_VERIFICAR_STEP_UP: 'auth:verificar-step-up',

  // Sprint 35 — Cliente recurrente (lookup por teléfono).
  CLIENTES_POR_TELEFONO: 'clientes:por-telefono',
  // Sprint 66 — Maestro de clientes: a cuántos se les puede escribir, y la baja.
  CLIENTES_RESUMEN: 'clientes:resumen',
  CLIENTES_CONSENTIMIENTO: 'clientes:consentimiento',
  CLIENTES_PARA_PUBLICIDAD: 'clientes:para-publicidad',

  // Sprint 36 — Reportes prediseñados (rango, mejor cliente, cancelados, comparativo).
  REPORTES_VENTAS_RANGO: 'reportes:ventas-rango',
  // Sprint 157 — Fuera de `reportes:*` a propósito: sólo el dueño (ver permisos.ts).
  CONTABILIDAD_PDF_INGRESOS: 'contabilidad:pdf-ingresos',
  // Sprint 170 — Fuera de `reportes:*` a propósito: sólo el dueño (ver permisos.ts).
  ESTADISTICAS_TABLERO: 'estadisticas:tablero',
  REPORTES_MEJOR_CLIENTE: 'reportes:mejor-cliente',
  REPORTES_CANCELADOS: 'reportes:cancelados',
  REPORTES_COMPARATIVO_SEMANA: 'reportes:comparativo-semana',
  // Sprint 36 — Diálogo nativo para elegir carpeta de respaldos sincronizada.
  BACKUP_ELEGIR_CARPETA_SYNC: 'backup:elegir-carpeta-sync',

  // Sprint 9.3 — métricas de uso para ProductosPage
  PRODUCTOS_METRICAS_USO_MES: 'productos:metricas-uso-mes',

  // Paquetes
  PAQUETES_LISTAR: 'paquetes:listar',
  PAQUETES_OBTENER: 'paquetes:obtener',
  PAQUETES_CREAR: 'paquetes:crear',
  PAQUETES_ACTUALIZAR: 'paquetes:actualizar',
  PAQUETES_ELIMINAR: 'paquetes:eliminar',

  // Mesas
  MESAS_LISTAR: 'mesas:listar',
  MESAS_CREAR: 'mesas:crear',
  MESAS_ACTUALIZAR: 'mesas:actualizar',
  MESAS_ELIMINAR: 'mesas:eliminar',
  /** @deprecated Sprint 10 — eliminado. El estado se calcula en mesaEstadoService. */
  MESAS_CAMBIAR_ESTADO: 'mesas:cambiar-estado',
  // Sprint 9.2 — fuera de servicio
  MESAS_MARCAR_FUERA_SERVICIO: 'mesas:marcar-fuera-servicio',
  MESAS_REACTIVAR: 'mesas:reactivar',

  // Pedidos
  PEDIDOS_CREAR: 'pedidos:crear',
  PEDIDOS_LISTAR: 'pedidos:listar',
  PEDIDOS_OBTENER: 'pedidos:obtener',
  PEDIDOS_LISTAR_POR_ESTADO: 'pedidos:listar-por-estado',
  PEDIDOS_MARCAR_LISTO: 'pedidos:marcar-listo',
  // Sprint 66 — Paso 7 del flujo de domicilio: "ya está empaquetado".
  PEDIDOS_MARCAR_EMPAQUETADO: 'pedidos:marcar-empaquetado',
  // Sprint 68 — Estado de una cuenta dividida (cuántas partes faltan por cobrar).
  PEDIDOS_DIVISION: 'pedidos:division',
  PEDIDOS_COBRAR: 'pedidos:cobrar',
  PEDIDOS_CANCELAR: 'pedidos:cancelar',
  PEDIDOS_CANCELAR_ITEM: 'pedidos:cancelar-item',
  PEDIDOS_MOVER_MESA: 'pedidos:mover-mesa',
  PEDIDOS_JUNTAR_MESAS: 'pedidos:juntar-mesas',
  PEDIDOS_SEPARAR_MESAS: 'pedidos:separar-mesas',
  PEDIDOS_DIVIDIR: 'pedidos:dividir',
  /**
   * Sprint 78 — ¿Este carrito se puede vender?
   *
   * Se pregunta al cerrar el carrito, no al final: el negocio capturó pedido, dirección y envío
   * para que el servidor lo rechazara en el último botón.
   */
  PEDIDOS_VALIDAR_CARRITO: 'pedidos:validar-carrito',

  // Sprint 76 — A domicilio: el tablero y la espera.
  /** Los pedidos a domicilio vivos, con el motivo por el que cada uno está esperando. */
  /**
   * Sprint 122 — La bandeja única: los pedidos vivos de los tres canales, en una consulta.
   * Reemplaza la fusión en cliente de tres canales, que dejaba a cocina con una bandeja
   * incompleta sin avisar (no tiene permiso sobre el tablero de domicilios).
   */
  PEDIDOS_BANDEJA: 'pedidos:bandeja',
  PEDIDOS_TABLERO_DOMICILIOS: 'pedidos:tablero-domicilios',
  /** Sprint 119 — Cuántos domicilios salieron hoy. Un número para el encabezado, no una columna. */
  PEDIDOS_DOMICILIOS_ENTREGADOS_HOY: 'pedidos:domicilios-entregados-hoy',
  /** Completar el costo del envío (y quién lo recoge) después de haber guardado el pedido. */
  PEDIDOS_ACTUALIZAR_ENVIO: 'pedidos:actualizar-envio',
  /** "Espéralo otro rato": prórroga explícita para el comprobante de un cliente. */
  PEDIDOS_PRORROGAR_PAGO: 'pedidos:prorrogar-pago',

  // Sprint 77 — A domicilio: el dinero.
  /** Qué se ha cobrado por llevar a ese rumbo (zonas configuradas + lo aprendido). */
  PEDIDOS_SUGERENCIA_ENVIO: 'pedidos:sugerencia-envio',
  /** Las zonas de reparto con su tarifa: los atajos al cotizar. */
  ZONAS_LISTAR: 'zonas:listar',
  ZONAS_CREAR: 'zonas:crear',
  ZONAS_ACTUALIZAR: 'zonas:actualizar',
  ZONAS_ELIMINAR: 'zonas:eliminar',
  /**
   * DT-072 — Dinero cobrado de pedidos que no se entregaron.
   *
   * Sección propia y **declarada pero sin asignar a ningún oficio**: así la matriz la deja sólo
   * para el dueño sin necesidad de una excepción escrita a mano. Es dinero saliendo de la caja.
   */
  DEVOLUCIONES_PENDIENTES: 'devoluciones:pendientes',
  DEVOLUCIONES_REGISTRAR: 'devoluciones:registrar',

  /**
   * Sprint 139 — *"Dijo efectivo y pagó con tarjeta."*
   *
   * **Sección propia y no `pedidos:`** a propósito: `caja` tiene el comodín `pedidos:*`, así que
   * un canal ahí dentro le llegaría solo, sin que nadie lo decidiera. Deshacer un cobro ya
   * registrado no es vender: es la acción con la que se taparía un faltante. Nace **sólo para el
   * dueño** por fail-closed, y el negocio se la puede dar a la caja con el mismo interruptor que
   * ya decidió si la caja puede cancelar.
   */
  CORRECCION_PAGO_REGISTRAR: 'correccion-pago:registrar',
  CORRECCION_PAGO_LISTAR: 'correccion-pago:listar',

  /**
   * Sprint 141 — Cobrar lo que falta cuando se le agregó algo a un pedido ya pagado.
   *
   * Va en `pedidos:` —y no en una sección propia como la corrección— porque **es vender**: la
   * cuenta subió y hay que cobrar lo nuevo. Quien puede cobrar puede cobrar esto; no deshace
   * nada ni toca lo ya registrado, sólo suma lo que el cliente acaba de pedir.
   */
  PEDIDOS_COBRAR_DIFERENCIA: 'pedidos:cobrar-diferencia',

  // Sprint 7.2 — Delivery con pago + cuenta solicitada
  PEDIDOS_ENVIAR_COCINA: 'pedidos:enviar-cocina',
  PEDIDOS_ADJUNTAR_COMPROBANTE: 'pedidos:adjuntar-comprobante',
  /**
   * Sprint 79 — Confirmar el pago sin imagen: "ya verifiqué el depósito en mi cuenta".
   *
   * El dinero a veces llega sin foto. La garantía sigue siendo que alguien verificó antes de
   * cocinar; lo que cambia es que esa verificación puede ser humana y queda firmada.
   */
  PEDIDOS_CONFIRMAR_PAGO_VERIFICADO: 'pedidos:confirmar-pago-verificado',
  PEDIDOS_MARCAR_CUENTA: 'pedidos:marcar-cuenta',
  PEDIDOS_ABRIR_COMPROBANTE: 'pedidos:abrir-comprobante',

  // Impresoras
  IMPRESORAS_LISTAR: 'impresoras:listar',

  // Tickets
  TICKET_GENERAR_PDF: 'ticket:generar-pdf',
  TICKET_ABRIR_PDF: 'ticket:abrir-pdf',
  TICKET_IMPRIMIR: 'ticket:imprimir',
  /** Sprint 56 — HTML del ticket para impresión desde el navegador (track web). */
  TICKET_HTML: 'ticket:html',
  // Sprint 58 — Impresión por agente local (ESC/POS).
  // Sprint 60 — Dejar de vender un producto, con su impacto en los paquetes.
  PRODUCTOS_IMPACTO_RETIRO: 'productos:impacto-retiro',
  /** Sprint 60 — Vínculos producto↔insumo que no cuadran (DT-038). */
  PRODUCTOS_REVISION_INSUMOS: 'productos:revision-insumos',
  /**
   * Sprint 81 — Productos que NO descuentan inventario, con el ingrediente que se les parece.
   *
   * El punto ciego de la revisión anterior: miraba los vínculos hechos, nunca los que faltan. La
   * dueña capturó sus bebidas dos veces —producto e ingrediente— y nadie las unió.
   */
  PRODUCTOS_SIN_INVENTARIO: 'productos:sin-inventario',
  /** Sprint 81 — Unir un producto con su ingrediente, en un clic y con confirmación humana. */
  PRODUCTOS_VINCULAR_INSUMO: 'productos:vincular-insumo',
  /** Sprint 60 — Qué paquetes rompería desactivar un insumo. Sólo informa. */
  INSUMOS_IMPACTO_DESACTIVAR: 'insumos:impacto-desactivar',
  PRODUCTOS_RETIRAR: 'productos:retirar',
  /** Sprint 63 — Crea de un golpe las porciones que se venden de un ingrediente. */
  // Sprint 72 — Crear productos desde el inventario, sin capturar todo dos veces.
  PRODUCTOS_CANDIDATOS_VENTA: 'productos:candidatos-venta',
  PRODUCTOS_CREAR_DESDE_INVENTARIO: 'productos:crear-desde-inventario',
  PRODUCTOS_CREAR_PORCIONES: 'productos:crearPorciones',
  /**
   * Sprint 82 — Dar de alta lo que se vende **y sus formatos**, en una sola captura.
   *
   * Propuesta del dueño: "da de alta el producto y al mismo tiempo sus formatos de venta, que
   * básicamente es su MENÚ". Elimina la segunda captura del ingrediente.
   */
  PRODUCTOS_ALTA_CON_FORMATOS: 'productos:alta-con-formatos',
  /** Sprint 82 — Agregar un tamaño a algo que ya se vende (el 3/4 que faltaba). */
  PRODUCTOS_AGREGAR_FORMATO: 'productos:agregar-formato',
  /**
   * Sprint 84 — Lo que se vende **agrupado como se capturó**, y su edición.
   *
   * El alta hablaba de "Frijoles Charros con sus tamaños" y la lista devolvía dos productos
   * sueltos pidiendo ingrediente y consumo: *"¿y ahora dónde quedó Frijoles Charros donde se veían
   * agrupados?"*.
   */
  PRODUCTOS_GRUPOS: 'productos:grupos',
  PRODUCTOS_ACTUALIZAR_GRUPO: 'productos:actualizar-grupo',
  /** Sprint 110 — Retirar del menú algo que ya no se vende, con su ingrediente. */
  PRODUCTOS_RETIRAR_GRUPO: 'productos:retirar-grupo',

  /**
   * Sprint 91 — Los apodos con los que el cliente pide.
   *
   * `listar` lo usa el POS para interpretar el chat, así que va con quien toma pedidos.
   * `aprender` también: nace de resolver un renglón, no de administrar el catálogo.
   */
  APODOS_LISTAR: 'apodos:listar',
  APODOS_APRENDER: 'apodos:aprender',
  APODOS_OLVIDAR: 'apodos:olvidar',

  // Sprint 63 (Fase 4) — Catálogo de unidades de medida. Eran tres valores fijos en el
  // código y el negocio preguntó dónde se daban de alta: en ningún lado.
  UNIDADES_LISTAR: 'unidades:listar',
  UNIDADES_LISTAR_TODAS: 'unidades:listarTodas',
  UNIDADES_CREAR: 'unidades:crear',
  UNIDADES_ACTUALIZAR: 'unidades:actualizar',
  PRODUCTOS_REACTIVAR: 'productos:reactivar',
  IMPRESION_ESTADO: 'impresion:estado',
  IMPRESION_ENCOLAR: 'impresion:encolar',
  IMPRESION_CODIGO_EMPAREJAMIENTO: 'impresion:codigo-emparejamiento',
  // Sprint 156 — Ver y quitar cajas. Sólo el dueño: no se declaran para ningún otro oficio.
  IMPRESION_CAJAS: 'impresion:cajas',
  IMPRESION_DESCONECTAR_CAJA: 'impresion:desconectar-caja',
  // Sprint 158 (DT-161) — Sólo el dueño, igual que los dos anteriores.
  IMPRESION_RECONOCER_CAJA: 'impresion:reconocer-caja',

  // Reportes
  REPORTES_VENTAS_DIA: 'reportes:ventas-dia',
  REPORTES_PRODUCTOS_TOP: 'reportes:productos-top',
  REPORTES_PAQUETES_TOP: 'reportes:paquetes-top',
  REPORTES_VENTAS_MESERO: 'reportes:ventas-mesero',
  /** Sprint 24 — Snapshot de aplicaciones de promo en el día actual (para wizard de cierre). */
  REPORTES_PROMOS_APLICADAS_HOY: 'reportes:promos-aplicadas-hoy',

  // Corte de caja
  CORTE_ABIERTO: 'corte:abierto',
  CORTE_ABRIR: 'corte:abrir',
  CORTE_CERRAR: 'corte:cerrar',
  CORTE_PDF: 'corte:pdf',
  // Sprint 70 — El efectivo esperado, calculado por el mismo código que cierra el corte.
  CORTE_ESPERADO: 'corte:esperado',
  // Sprint 160 — La jornada cambia sola y se pregunta el conteo de ayer.
  CORTE_JORNADA: 'corte:jornada',
  CORTE_DECLARAR_CONTEO: 'corte:declarar-conteo',
  // Sprint 163 — El aviso "va en camino" al celular del cliente (sólo dueño).
  AVISOS_ESTADO: 'avisos:estado',
  AVISOS_CONFIGURAR: 'avisos:configurar',
  // Sprint 164 — Pedidos de días pasados sin concluir: "¿qué pasó con ellos?" (sólo dueño).
  SIN_CONCLUIR_LISTAR: 'sin-concluir:listar',
  SIN_CONCLUIR_RESOLVER: 'sin-concluir:resolver',
  // Sprint 167 — Privacidad de los clientes: datos del aviso y borrar a quien lo pida (sólo dueño).
  PRIVACIDAD_AJUSTES: 'privacidad:ajustes',
  PRIVACIDAD_GUARDAR: 'privacidad:guardar',
  PRIVACIDAD_BUSCAR_CLIENTE: 'privacidad:buscar-cliente',
  PRIVACIDAD_BORRAR_CLIENTE: 'privacidad:borrar-cliente',

  // Backups
  BACKUP_LISTAR: 'backup:listar',
  BACKUP_CREAR: 'backup:crear',
  BACKUP_ABRIR_CARPETA: 'backup:abrir-carpeta',
  // Sprint 12 — Restore + verificación
  BACKUP_VERIFICAR: 'backup:verificar',
  BACKUP_RESTAURAR: 'backup:restaurar',

  // Updates
  UPDATE_ESTADO: 'update:estado',
  UPDATE_INSTALAR: 'update:instalar',
  UPDATE_CHECK: 'update:check',

  // Errors
  ERROR_REPORTAR: 'error:reportar',

  // Sprint 12 — Credenciales SMTP cifradas con safeStorage
  SMTP_ESTADO: 'smtp:estado',
  SMTP_GUARDAR: 'smtp:guardar',
  SMTP_BORRAR: 'smtp:borrar',
  SMTP_PROBAR: 'smtp:probar',

  // Sprint 7 — Inventario por insumos
  INSUMOS_LISTAR: 'insumos:listar',
  INSUMOS_OBTENER: 'insumos:obtener',
  INSUMOS_CREAR: 'insumos:crear',
  INSUMOS_ACTUALIZAR: 'insumos:actualizar',
  INSUMOS_ELIMINAR: 'insumos:eliminar',
  INSUMOS_CARGA_DIARIA: 'insumos:carga-diaria',
  INSUMOS_REGISTRAR_MERMA: 'insumos:registrar-merma',
  INSUMOS_REGISTRAR_AJUSTE: 'insumos:registrar-ajuste',
  /**
   * Sprint 79 — "Llegó más": reponer existencia sin salir de la venta, nombrando el producto.
   *
   * Se le acabó la picaña a media venta de paquete y la reposición vivía en otra pantalla, en
   * lenguaje de ingredientes. Este canal recibe el producto que el operador tiene enfrente.
   */
  INSUMOS_REPONER_POR_PRODUCTO: 'insumos:reponer-por-producto',
  INSUMOS_STOCK_CRITICO: 'insumos:stock-critico',
  INSUMOS_BITACORA: 'insumos:bitacora',
  // Sprint 13.1 — Histórico del día por insumo
  INSUMOS_HISTORICO_DIA: 'insumos:historico-dia',
  /** Sprint 25 — Sugerencia de carga inicial basada en lo vendido el día anterior. */
  INSUMOS_SUGERENCIA_CARGA_DEL_DIA: 'insumos:sugerencia-carga-del-dia',


  // Sprint 7 — Cierre del día
  CIERRE_DIA_PREVIEW: 'cierre-dia:preview',
  /** Sprint 58 — sólo la actividad viva (mesas/cocina/delivery), sin el recorrido de insumos. */
  CIERRE_DIA_PENDIENTES: 'cierre-dia:pendientes',
  CIERRE_DIA_EJECUTAR: 'cierre-dia:ejecutar',
  CIERRE_DIA_HISTORIAL: 'cierre-dia:historial',
  CIERRE_DIA_PDF: 'cierre-dia:pdf',
  // Sprint 42 — marca operativa "día cerrado" (≠ snapshot CierreDia).
  CIERRE_DIA_MARCAR_CERRADO: 'cierre-dia:marcar-cerrado',
  CIERRE_DIA_ESTA_CERRADO_HOY: 'cierre-dia:esta-cerrado-hoy',
  // Sprint 7.5 — Promociones
  PROMOCIONES_LISTAR: 'promociones:listar',
  PROMOCIONES_LISTAR_VIGENTES: 'promociones:listar-vigentes',
  PROMOCIONES_OBTENER: 'promociones:obtener',
  PROMOCIONES_CREAR: 'promociones:crear',
  PROMOCIONES_ACTUALIZAR: 'promociones:actualizar',
  PROMOCIONES_ELIMINAR: 'promociones:eliminar',
  PROMOCIONES_SIMULAR: 'promociones:simular',

  // Sprint 8 — Despacho a repartidor
  PEDIDOS_ASIGNAR_REPARTIDOR: 'pedidos:asignar-repartidor',
  PEDIDOS_MARCAR_ENTREGADO: 'pedidos:marcar-entregado',
  PEDIDOS_MARCAR_NO_ENTREGADO: 'pedidos:marcar-no-entregado',
  /**
   * Sprint 100 — Cerrar un pedido entregado que ya estaba pagado.
   *
   * No cobra nada: pone el `estado` de acuerdo con el `estadoPago` que ya decía "pagado". Es la
   * salida de los pedidos que quedaron en limbo antes de que se cerrara la causa.
   */
  PEDIDOS_CERRAR_ENTREGADO_PAGADO: 'pedidos:cerrar-entregado-pagado',

  // Sprint 12.1d — Entregado en mesa (comer_aqui)
  PEDIDOS_MARCAR_ENTREGADO_MESA: 'pedidos:marcar-entregado-mesa',

  /**
   * Sprint 111 — El mostrador se lee por entrega, no por estado.
   *
   * Cobrar deja el pedido en `'pagado'`, que es terminal: con el cobro anticipado del Sprint 108
   * la tarjeta desaparecía del tablero apenas se cobraba. Estos dos canales preguntan lo que de
   * verdad importa ahí — **si el cliente ya se lo llevó**.
   */
  PEDIDOS_LISTAR_MOSTRADOR: 'pedidos:listar-mostrador',
  /** Sprint 145 — Las ventas ya cobradas del periodo de caja, para poder corregir una. */
  PEDIDOS_VENTAS_DEL_DIA: 'pedidos:ventas-del-dia',
  PEDIDOS_MARCAR_ENTREGADO_MOSTRADOR: 'pedidos:marcar-entregado-mostrador',

  // Sprint 28 — Agregar productos a un pedido de mesa en curso
  PEDIDOS_AGREGAR_ITEMS: 'pedidos:agregar-items',

  // Sprint 29 — Volver a juntar una cuenta dividida
  PEDIDOS_REUNIFICAR: 'pedidos:reunificar',

  // Sprint 172 — Cada mesero, sus mesas: la entrega de turno, su globo y su bitácora.
  PEDIDOS_ENTREGAR_TURNO: 'pedidos:entregar-turno',
  PEDIDOS_ENTREGAS_RECIENTES: 'pedidos:entregas-recientes',
  PEDIDOS_BITACORA_TURNOS: 'pedidos:bitacora-turnos',

  // Sprint 29 — Resolver enlace de ubicación (Google Maps) que el cliente manda
  UBICACION_RESOLVER_ENLACE: 'ubicacion:resolver-enlace',

  // Sprint 12.1b — Propinas
  PROPINAS_POZO: 'propinas:pozo',
  PROPINAS_LISTAR_PENDIENTES: 'propinas:listar-pendientes',
  PROPINAS_REASIGNAR_PAGO: 'propinas:reasignar-pago',
  PROPINAS_SIMULAR_REPARTO: 'propinas:simular-reparto',
  PROPINAS_EJECUTAR_REPARTO: 'propinas:ejecutar-reparto',
  PROPINAS_CANCELAR_REPARTO: 'propinas:cancelar-reparto',
  PROPINAS_MARCAR_ENTREGADA: 'propinas:marcar-entregada',
  PROPINAS_HISTORIAL: 'propinas:historial',
  // Sprint 12.1c — rango natural de pagos pendientes (para auto-completar wizard)
  PROPINAS_RANGO_PENDIENTES: 'propinas:rango-pendientes',
  // Sprint 41 — resumen rápido por rango (SUM+COUNT) para tarjetas de preset.
  PROPINAS_RESUMEN_POR_RANGO: 'propinas:resumen-por-rango',

  // Sprint 12.1b — Movimientos de caja
  MOVIMIENTOS_CAJA_REGISTRAR: 'movimientos-caja:registrar',
  MOVIMIENTOS_CAJA_LISTAR_DEL_CORTE: 'movimientos-caja:listar-del-corte',
  MOVIMIENTOS_CAJA_HISTORIAL: 'movimientos-caja:historial',

  // Sprint 12.1b — Listar todos los usuarios (incluye sin login)
  USUARIOS_LISTAR_TODOS: 'usuarios:listar-todos',
  /** Sprint 64 — Volver a contratar: la gente regresa. */
  USUARIOS_REACTIVAR: 'usuarios:reactivar',
} as const;

export type IpcChannel = (typeof IPC_CHANNELS)[keyof typeof IPC_CHANNELS];

/**
 * Pasos del wizard de onboarding.
 */
export const ONBOARDING_PASOS = [1, 2, 3, 4, 5] as const;
export type OnboardingPaso = (typeof ONBOARDING_PASOS)[number];

export const APP_VERSION = '1.31.0';
